import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type {
  MutationCreateCustomFieldArgs,
  MutationDeleteCustomFieldArgs,
  MutationUpdateCustomFieldArgs,
  MutationDeleteCustomFieldOptionArgs,
  MutationAddCustomFieldOptionArgs,
  MutationUpdateCustomFieldOptionArgs,
  MutationUpdateCustomFieldItemArgs,
} from '../generated';
import { token } from '@trello/session-cookie';
import { fetch } from '@trello/fetch';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { parseNetworkError } from '@trello/graphql-error-handling';
import type { TrelloRestResolver } from '../types';
import { isQueryInfo } from '../isQueryInfo';

export const createCustomField: TrelloRestResolver<MutationCreateCustomFieldArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { name, idModel, modelType, display, type, options } = args;
  const response = await fetch(`/1/customFields`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      display_cardFront: display.cardFront,
      idModel,
      modelType,
      name,
      options,
      type,
      token: context.token || token,
    }),
  });

  const customField = await response.json();

  return prepareDataForApolloCache(customField, rootNode);
};

export const deleteCustomField: TrelloRestResolver<MutationDeleteCustomFieldArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { idCustomField } = args;
  const response = await fetch(`/1/customFields/${idCustomField}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  const customField = await response.json();

  return prepareDataForApolloCache(customField, rootNode);
};

export const updateCustomField: TrelloRestResolver<MutationUpdateCustomFieldArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { name, idCustomField, display, pos } = args;
  const response = await fetch(`/1/customFields/${idCustomField}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      'display/cardFront': display?.cardFront,
      name,
      pos,
      token: context.token || token,
    }),
  });

  const customField = await response.json();

  return prepareDataForApolloCache(customField, rootNode);
};

export const deleteCustomFieldOption: TrelloRestResolver<MutationDeleteCustomFieldOptionArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { idCustomField, idCustomFieldOption } = args;
  const response = await fetch(
    `/1/customFields/${idCustomField}/options/${idCustomFieldOption}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  const customField = await response.json();

  return prepareDataForApolloCache(customField, rootNode);
};

export const addCustomFieldOption: TrelloRestResolver<MutationAddCustomFieldOptionArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { name, idCustomField } = args;
  const response = await fetch(`/1/customFields/${idCustomField}/options`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      value: { text: name },
      token: context.token || token,
    }),
  });

  const customField = await response.json();

  return prepareDataForApolloCache(customField, rootNode);
};

export const updateCustomFieldOption: TrelloRestResolver<MutationUpdateCustomFieldOptionArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const { idCustomField, idCustomFieldOption, name, color, pos } = args;
  const response = await fetch(
    `/1/customFields/${idCustomField}/options/${idCustomFieldOption}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        color,
        value: name ? { text: name } : undefined,
        pos,
        token: context.token || token,
      }),
    },
  );

  const customField = await response.json();

  return prepareDataForApolloCache(customField, rootNode);
};

export const updateCustomFieldItem: TrelloRestResolver<MutationUpdateCustomFieldItemArgs> = async (
  obj,
  { idCard, idCustomField, value, idValue },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const apiUrl = `/1/card/${idCard}/customField/${idCustomField}/item`;
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      idValue,
      value,
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const customFieldItem = await response.json();

  return prepareDataForApolloCache(customFieldItem, rootNode);
};
